import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import useAuthStore from '../lib/auth-store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Search, MapPin, Phone, PhoneCall, Navigation, ListChecks, ArrowRight,
  ArrowLeft, Building2, Star, Globe, Clock, CheckCircle2, XCircle,
  ChevronRight, Plus, Loader2, MessageSquare, Target, Route,
  Car, Footprints, GripVertical, CalendarDays, User, Briefcase,
  Send, RotateCcw, ExternalLink, Zap, CircleDot, AlertTriangle,
  BarChart3, TrendingUp, Users, BookOpen, ChevronDown, ChevronUp, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════
// MAIN PAGE — State Machine
// ═══════════════════════════════════════════════════════════

export default function ProspectingPage() {
  const { token } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState('home'); // home, search_results, lists, list_detail, call_queue, canvass, coach, performance
  const [niches, setNiches] = useState([]);
  const [lists, setLists] = useState([]);
  const [followUps, setFollowUps] = useState([]);

  // Search state
  const [niche, setNiche] = useState('');
  const [radius, setRadius] = useState('5');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);

  // List state
  const [activeList, setActiveList] = useState(null);
  const [currentProspect, setCurrentProspect] = useState(null);
  const [aiBrief, setAiBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [activeRoute, setActiveRoute] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Load niches on mount
  useEffect(() => {
    fetch(`${API}/api/prospecting/niches`, { headers })
      .then(r => r.json()).then(setNiches).catch(() => { /* ignore */ });
    loadLists();
    loadFollowUps();
  }, []);

  // Deep-link support: /prospecting?list_id=XYZ jumps straight into that list's call queue
  useEffect(() => {
    const targetListId = searchParams.get('list_id');
    if (!targetListId || lists.length === 0 || activeList) return;
    const match = lists.find((l) => l.list_id === targetListId);
    if (match) startCallQueue(match);
  }, [lists, searchParams]);

  const loadLists = async () => {
    try {
      const r = await fetch(`${API}/api/prospecting/lists`, { headers });
      setLists(await r.json());
    } catch { /* ignore */ }
  };

  const loadFollowUps = async () => {
    try {
      const r = await fetch(`${API}/api/prospecting/follow-ups`, { headers });
      const data = await r.json();
      setFollowUps(data.follow_ups || []);
    } catch { /* ignore */ }
  };

  // Get user location
  const getLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          setLocationLoading(false);
          toast.success('Location detected');
        },
        () => {
          // Fallback: Miami
          setUserLocation({ lat: 25.7617, lng: -80.1918 });
          setLocationLoading(false);
          toast.info('Using default location (Miami)');
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setUserLocation({ lat: 25.7617, lng: -80.1918 });
      setLocationLoading(false);
    }
  };

  // Search businesses
  const handleSearch = async () => {
    if (!niche) { toast.error('Select a business type'); return; }
    if (!userLocation) { toast.error('Enable location first'); return; }
    setSearching(true);
    try {
      const r = await fetch(`${API}/api/prospecting/search`, {
        method: 'POST', headers,
        body: JSON.stringify({
          niche, lat: userLocation.lat, lng: userLocation.lng,
          radius_miles: parseFloat(radius),
        }),
      });
      if (r.status === 429) {
        const err = await r.json();
        toast.error(err.detail || 'Monthly search limit reached');
        setSearching(false);
        return;
      }
      const data = await r.json();
      if (data.usage) {
        toast.info(`${data.usage.remaining} searches remaining this month`);
      }
      setSearchResults(data);
      setView('search_results');
    } catch (err) { toast.error('Search failed'); }
    setSearching(false);
  };

  // Build list from search
  const buildList = async (businesses) => {
    const nicheLabel = niches.find(n => n.id === niche)?.label || niche;
    const name = `${nicheLabel} - ${radius}mi`;
    try {
      const r = await fetch(`${API}/api/prospecting/lists`, {
        method: 'POST', headers,
        body: JSON.stringify({
          name, niche,
          center_lat: userLocation.lat, center_lng: userLocation.lng,
          radius_miles: parseFloat(radius), businesses,
        }),
      });
      const data = await r.json();
      toast.success(`List "${name}" created with ${data.total_count} prospects`);
      loadLists();
      setActiveList(data);
      setView('list_detail');
    } catch (err) { toast.error('Failed to create list'); }
  };

  // Start call queue
  const startCallQueue = async (list) => {
    setActiveList(list);
    setView('call_queue');
    await loadCurrentProspect(list.list_id);
  };

  // Load current prospect in queue
  const loadCurrentProspect = async (listId) => {
    try {
      const r = await fetch(`${API}/api/prospecting/lists/${listId}/current`, { headers });
      const data = await r.json();
      setCurrentProspect(data);
      if (!data.complete && data.business) {
        loadAiBrief(data.business, data.activity_history || [], 'call');
        // Fetch phone if missing
        if (!data.business.phone && data.business.place_id) {
          fetchPlacePhone(listId, data.business.place_id, data.index);
        }
      }
    } catch { toast.error('Failed to load prospect'); }
  };

  const fetchPlacePhone = async (listId, placeId, idx) => {
    try {
      const r = await fetch(`${API}/api/prospecting/place-detail/${placeId}`, { headers });
      const detail = await r.json();
      if (detail.phone) {
        setCurrentProspect(prev => {
          if (!prev || prev.complete) return prev;
          const biz = { ...prev.business, phone: detail.phone, website: detail.website };
          return { ...prev, business: biz };
        });
      }
    } catch { /* ignore */ }
  };

  // Load AI brief
  const loadAiBrief = async (business, history, mode) => {
    setBriefLoading(true);
    setAiBrief(null);
    try {
      const r = await fetch(`${API}/api/prospecting/ai-brief`, {
        method: 'POST', headers,
        body: JSON.stringify({ business, activity_history: history, work_mode: mode }),
      });
      setAiBrief(await r.json());
    } catch { /* ignore */ }
    setBriefLoading(false);
  };

  // Start canvass
  const startCanvass = async (list, mode = 'driving') => {
    setActiveList(list);
    try {
      const r = await fetch(`${API}/api/prospecting/lists/${list.list_id}/route`, {
        method: 'POST', headers,
        body: JSON.stringify({ travel_mode: mode }),
      });
      if (!r.ok) throw new Error();
      const route = await r.json();
      setActiveRoute(route);
      setView('canvass');
    } catch { toast.error('Failed to create route'); }
  };

  // ═══════════════════════════════════════════════════════════
  // RENDER VIEWS
  // ═══════════════════════════════════════════════════════════

  return (
    <div className="max-w-2xl mx-auto pb-24" data-testid="prospecting-page">
      {view === 'home' && (
        <HomeView
          niches={niches} niche={niche} setNiche={setNiche}
          radius={radius} setRadius={setRadius}
          userLocation={userLocation} getLocation={getLocation}
          locationLoading={locationLoading}
          searching={searching} handleSearch={handleSearch}
          lists={lists} followUps={followUps}
          onOpenList={(l) => { setActiveList(l); setView('list_detail'); }}
          onViewLists={() => { loadLists(); setView('lists'); }}
          onStartCall={startCallQueue}
          loadFollowUps={loadFollowUps}
          headers={headers}
          onViewPerformance={() => setView('performance')}
        />
      )}

      {view === 'search_results' && searchResults && (
        <SearchResultsView
          results={searchResults} niche={niche} radius={radius}
          niches={niches}
          onBack={() => setView('home')}
          onBuildList={buildList}
        />
      )}

      {view === 'lists' && (
        <ListsView
          lists={lists}
          onBack={() => setView('home')}
          onOpenList={(l) => { setActiveList(l); setView('list_detail'); }}
          onStartCall={startCallQueue}
          onStartCanvass={startCanvass}
        />
      )}

      {view === 'list_detail' && activeList && (
        <ListDetailView
          list={activeList} headers={headers}
          onBack={() => { loadLists(); setView('lists'); }}
          onStartCall={() => startCallQueue(activeList)}
          onStartCanvass={(mode) => startCanvass(activeList, mode)}
          onViewMap={() => {}}
        />
      )}

      {view === 'call_queue' && (
        <CallQueueView
          listId={activeList?.list_id || activeList?.list_id}
          prospect={currentProspect}
          aiBrief={aiBrief} briefLoading={briefLoading}
          headers={headers}
          onBack={() => { loadLists(); setView('lists'); }}
          onNext={() => loadCurrentProspect(activeList?.list_id)}
          onConvert={(placeId) => convertProspect(activeList?.list_id, placeId)}
          onCoachMe={() => setView('coach')}
        />
      )}

      {view === 'canvass' && activeRoute && (
        <CanvassView
          route={activeRoute} list={activeList}
          headers={headers}
          onBack={() => { loadLists(); setView('lists'); }}
          setActiveRoute={setActiveRoute}
          loadAiBrief={loadAiBrief}
          aiBrief={aiBrief} briefLoading={briefLoading}
        />
      )}

      {view === 'coach' && (
        <CoachMeView
          headers={headers}
          business={currentProspect?.business}
          onBack={() => setView('call_queue')}
        />
      )}

      {view === 'performance' && (
        <PerformanceView
          headers={headers}
          onBack={() => setView('home')}
        />
      )}
    </div>
  );

  async function convertProspect(listId, placeId) {
    try {
      const r = await fetch(`${API}/api/prospecting/lists/${listId}/convert/${placeId}`, {
        method: 'POST', headers,
        body: JSON.stringify({ playbook: 'payment_savings' }),
      });
      const data = await r.json();
      toast.success(data.is_existing ? 'Linked to existing lead' : 'Converted to lead!');
    } catch { toast.error('Convert failed'); }
  }
}


// ═══════════════════════════════════════════════════════════
// HOME VIEW — Target + Search + Follow-ups
// ═══════════════════════════════════════════════════════════

function HomeView({ niches, niche, setNiche, radius, setRadius, userLocation, getLocation, locationLoading, searching, handleSearch, lists, followUps, onOpenList, onViewLists, onStartCall, loadFollowUps, headers, onViewPerformance }) {
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/calendar/events?days_ahead=7&max_results=5`, { headers })
      .then(r => r.ok ? r.json() : { events: [] })
      .then(d => setUpcomingMeetings(d.events || []))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-6 p-4" data-testid="prospecting-home">
      <div className="flex items-center justify-between pt-2 pb-4">
        <div>
          <h1 className="text-2xl font-bold">Prospecting</h1>
          <p className="text-sm text-muted-foreground mt-1">Find businesses, build lists, start selling</p>
        </div>
        <Button variant="outline" size="sm" onClick={onViewPerformance} data-testid="performance-btn">
          <BarChart3 className="w-4 h-4 mr-1.5" /> Stats
        </Button>
      </div>

      {/* Search Card */}
      <div className="rounded-2xl border bg-card p-5 space-y-4" data-testid="search-card">
        <h2 className="text-base font-semibold flex items-center gap-2">
          <Target className="w-4 h-4" /> What are you targeting?
        </h2>
        <Select value={niche} onValueChange={setNiche}>
          <SelectTrigger data-testid="niche-select"><SelectValue placeholder="Select business type" /></SelectTrigger>
          <SelectContent>
            {niches.map(n => (
              <SelectItem key={n.id} value={n.id}>{n.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <h2 className="text-base font-semibold flex items-center gap-2">
          <MapPin className="w-4 h-4" /> Where?
        </h2>
        <div className="flex gap-2">
          <Button variant={userLocation ? "secondary" : "default"} className="flex-1"
            onClick={getLocation} disabled={locationLoading} data-testid="location-btn">
            {locationLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Navigation className="w-4 h-4 mr-2" />}
            {userLocation ? 'Location Set' : 'Use Current Location'}
          </Button>
          <Select value={radius} onValueChange={setRadius}>
            <SelectTrigger className="w-24" data-testid="radius-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['1','2','3','5','10','15','20','25'].map(r => (
                <SelectItem key={r} value={r}>{r} mi</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button className="w-full h-12 text-base font-semibold" onClick={handleSearch}
          disabled={searching || !niche || !userLocation} data-testid="find-btn">
          {searching ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Searching...</> : <><Search className="w-5 h-5 mr-2" /> FIND BUSINESSES</>}
        </Button>
      </div>

      {/* Follow-ups Due */}
      {followUps.length > 0 && (
        <div className="rounded-2xl border bg-card p-4" data-testid="follow-ups-card">
          <h2 className="text-base font-semibold flex items-center gap-2 mb-3">
            <CalendarDays className="w-4 h-4" /> Follow-Ups Due
            <Badge variant="destructive" className="ml-auto">{followUps.length}</Badge>
          </h2>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {followUps.slice(0, 5).map(fu => (
              <div key={fu.follow_up_id} className={`flex items-center gap-3 p-2 rounded-lg text-sm ${fu.overdue ? 'bg-red-50 border border-red-200' : 'bg-muted/50'}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{fu.business_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {fu.disposition_type?.replace(/_/g, ' ')} · {fu.follow_up_date}
                    {fu.follow_up_time && ` at ${fu.follow_up_time}`}
                  </p>
                </div>
                {fu.business_phone && (
                  <a href={`tel:${fu.business_phone}`} className="p-2 rounded-full bg-green-100 text-green-700">
                    <Phone className="w-4 h-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Calendar Meetings */}
      {upcomingMeetings.length > 0 && (
        <div className="rounded-2xl border bg-card p-4" data-testid="upcoming-meetings">
          <h2 className="text-base font-semibold flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4" /> Upcoming Meetings
            <Badge variant="secondary" className="ml-auto">{upcomingMeetings.length}</Badge>
          </h2>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {upcomingMeetings.map(evt => {
              const startStr = evt.start?.dateTime
                ? new Date(evt.start.dateTime).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                : evt.start?.date || '';
              return (
                <a
                  key={evt.event_id}
                  href={evt.html_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-2 rounded-lg bg-blue-50 border border-blue-100 text-sm hover:bg-blue-100 transition-colors"
                  data-testid={`meeting-${evt.event_id}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <CalendarDays className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate text-blue-900">{evt.summary}</p>
                    <p className="text-xs text-blue-600">{startStr}{evt.location ? ` · ${evt.location}` : ''}</p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Lists */}
      {lists.length > 0 && (
        <div className="rounded-2xl border bg-card p-4" data-testid="recent-lists">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold flex items-center gap-2">
              <ListChecks className="w-4 h-4" /> My Lists
            </h2>
            <Button variant="ghost" size="sm" onClick={onViewLists}>View All <ChevronRight className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-2">
            {lists.slice(0, 3).map(l => (
              <div key={l.list_id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 cursor-pointer hover:bg-muted/60 transition-colors"
                onClick={() => onOpenList(l)} data-testid={`list-${l.list_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{l.name}</p>
                  <p className="text-xs text-muted-foreground">{l.total_count} prospects · {l.niche?.replace(/_/g, ' ')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="default" size="sm" onClick={(e) => { e.stopPropagation(); onStartCall(l); }}
                    data-testid={`start-call-${l.list_id}`}>
                    <PhoneCall className="w-3.5 h-3.5 mr-1" /> Call
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// SEARCH RESULTS
// ═══════════════════════════════════════════════════════════

function SearchResultsView({ results, niche, radius, niches, onBack, onBuildList }) {
  const nicheLabel = niches.find(n => n.id === niche)?.label || niche;
  const newBiz = results.businesses.filter(b => !b.is_duplicate);

  return (
    <div className="p-4 space-y-4" data-testid="search-results">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
        <div>
          <h2 className="text-lg font-bold">{results.total} Businesses Found</h2>
          <p className="text-xs text-muted-foreground">{nicheLabel} · {radius} miles · {results.new_count} new, {results.duplicate_count} existing</p>
        </div>
      </div>

      <Button className="w-full h-12 text-base font-semibold" onClick={() => onBuildList(newBiz)}
        disabled={newBiz.length === 0} data-testid="build-list-btn">
        <Plus className="w-5 h-5 mr-2" /> BUILD LIST ({newBiz.length} prospects)
      </Button>

      <div className="space-y-2 max-h-[60vh] overflow-y-auto">
        {results.businesses.map((b, i) => (
          <div key={b.place_id || i}
            className={`p-3 rounded-xl border ${b.is_duplicate ? 'opacity-50 border-dashed' : 'bg-card'}`}
            data-testid={`result-${i}`}>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{b.name}</p>
                <p className="text-xs text-muted-foreground truncate">{b.address}</p>
                <div className="flex items-center gap-3 mt-1">
                  {b.rating && (
                    <span className="flex items-center gap-0.5 text-xs">
                      <Star className="w-3 h-3 text-amber-500 fill-amber-500" /> {b.rating}
                      <span className="text-muted-foreground">({b.review_count})</span>
                    </span>
                  )}
                  {b.is_duplicate && (
                    <Badge variant="outline" className="text-[10px]">{b.duplicate_reason}</Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// LISTS VIEW
// ═══════════════════════════════════════════════════════════

function ListsView({ lists, onBack, onOpenList, onStartCall, onStartCanvass }) {
  return (
    <div className="p-4 space-y-4" data-testid="lists-view">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
        <h2 className="text-lg font-bold">Prospect Lists</h2>
      </div>
      {lists.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <ListChecks className="w-12 h-12 mx-auto mb-3 opacity-20" />
          <p className="text-sm">No lists yet. Search and build your first list.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {lists.map(l => {
            const stats = l.stats || {};
            return (
              <div key={l.list_id} className="rounded-xl border bg-card p-4" data-testid={`list-card-${l.list_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="cursor-pointer" onClick={() => onOpenList(l)}>
                    <p className="font-semibold">{l.name}</p>
                    <p className="text-xs text-muted-foreground">{l.total_count} prospects · {l.niche?.replace(/_/g, ' ')}</p>
                  </div>
                </div>
                {/* Status pills */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {stats.not_contacted > 0 && <Badge variant="outline" className="text-[10px]">{stats.not_contacted} New</Badge>}
                  {stats.attempted > 0 && <Badge variant="secondary" className="text-[10px]">{stats.attempted} Attempted</Badge>}
                  {stats.connected > 0 && <Badge className="text-[10px] bg-blue-100 text-blue-800">{stats.connected} Connected</Badge>}
                  {stats.interested > 0 && <Badge className="text-[10px] bg-green-100 text-green-800">{stats.interested} Interested</Badge>}
                  {stats.appointment > 0 && <Badge className="text-[10px] bg-purple-100 text-purple-800">{stats.appointment} Appointment</Badge>}
                  {stats.converted > 0 && <Badge className="text-[10px] bg-emerald-100 text-emerald-800">{stats.converted} Converted</Badge>}
                </div>
                {/* Action buttons */}
                <div className="grid grid-cols-3 gap-2">
                  <Button size="sm" onClick={() => onStartCall(l)} data-testid={`call-list-${l.list_id}`}>
                    <PhoneCall className="w-3.5 h-3.5 mr-1" /> Call
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => onStartCanvass(l)} data-testid={`canvass-list-${l.list_id}`}>
                    <Route className="w-3.5 h-3.5 mr-1" /> D2D
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onOpenList(l)}>
                    <ListChecks className="w-3.5 h-3.5 mr-1" /> View
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// LIST DETAIL
// ═══════════════════════════════════════════════════════════

function ListDetailView({ list, headers, onBack, onStartCall, onStartCanvass }) {
  const [fullList, setFullList] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/prospecting/lists/${list.list_id}`, { headers })
      .then(r => r.json()).then(setFullList).catch(() => {});
  }, [list.list_id]);

  const businesses = fullList?.businesses || [];

  return (
    <div className="p-4 space-y-4" data-testid="list-detail-view">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
        <div>
          <h2 className="text-lg font-bold">{list.name}</h2>
          <p className="text-xs text-muted-foreground">{list.total_count} prospects</p>
        </div>
      </div>

      <div className="text-center py-3">
        <p className="text-sm font-medium mb-3">How do you want to work this list?</p>
        <div className="grid grid-cols-3 gap-3">
          <Button className="h-16 flex-col gap-1" onClick={onStartCall} data-testid="work-call">
            <PhoneCall className="w-5 h-5" />
            <span className="text-xs">Call</span>
          </Button>
          <Button className="h-16 flex-col gap-1" variant="secondary" onClick={() => onStartCanvass('driving')} data-testid="work-canvass">
            <Route className="w-5 h-5" />
            <span className="text-xs">Canvass D2D</span>
          </Button>
          <Button className="h-16 flex-col gap-1" variant="outline" onClick={() => {
            if (businesses.length > 0 && businesses[0].lat) {
              window.open(`https://www.google.com/maps/search/${encodeURIComponent(list.niche?.replace(/_/g,' '))}/@${businesses[0].lat},${businesses[0].lng},14z`, '_blank');
            }
          }} data-testid="work-map">
            <MapPin className="w-5 h-5" />
            <span className="text-xs">View Map</span>
          </Button>
        </div>
      </div>

      <div className="space-y-2 max-h-[55vh] overflow-y-auto">
        {businesses.map((b, i) => (
          <div key={b.place_id || i} className="flex items-center gap-3 p-3 rounded-xl border bg-card">
            <StatusDot status={b.status} />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{b.name}</p>
              <p className="text-xs text-muted-foreground truncate">{b.address}</p>
              {b.disposition && (
                <Badge variant="outline" className="text-[10px] mt-0.5">{b.disposition.replace(/_/g, ' ')}</Badge>
              )}
            </div>
            {b.phone && (
              <a href={`tel:${b.phone}`} className="p-2 rounded-full bg-green-100 text-green-700 flex-shrink-0">
                <Phone className="w-4 h-4" />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// CALL QUEUE — Power dialer style, one at a time
// ═══════════════════════════════════════════════════════════

function DiscoveryScriptCard({ businessName, niche, headers }) {
  const [open, setOpen] = useState(false);
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(false);
  const [personalizing, setPersonalizing] = useState(false);
  const [personalized, setPersonalized] = useState(false);

  const loadScript = async () => {
    if (script) { setOpen((o) => !o); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/prospecting/discovery-script?niche=${encodeURIComponent(niche || 'general')}`, { headers });
      const data = await r.json();
      setScript(data);
      setOpen(true);
    } catch { toast.error('Could not load call script'); }
    setLoading(false);
  };

  const handlePersonalize = async () => {
    setPersonalizing(true);
    try {
      const r = await fetch(`${API}/api/prospecting/discovery-script/personalize`, {
        method: 'POST', headers,
        body: JSON.stringify({ business_name: businessName, niche: niche || 'general' }),
      });
      const data = await r.json();
      setScript(data);
      setPersonalized(!!data.personalized);
      if (!data.personalized) toast.info('AI personalization unavailable right now — showing the standard script.');
      else toast.success('Script personalized for this call');
    } catch { toast.error('Personalize failed'); }
    setPersonalizing(false);
  };

  return (
    <div className="rounded-2xl border bg-card p-4" data-testid="discovery-script-card">
      <button type="button" onClick={loadScript} className="w-full flex items-center justify-between" data-testid="discovery-script-toggle">
        <h3 className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1.5">
          <BookOpen className="w-3.5 h-3.5" /> MSC Sales Method — Call Script
        </h3>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />)}
      </button>
      {open && script && (
        <div className="mt-3 space-y-3">
          <Button size="sm" variant="outline" onClick={handlePersonalize} disabled={personalizing} data-testid="personalize-script-btn">
            {personalizing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
            {personalized ? 'Re-personalize for this call' : 'Personalize with AI'}
          </Button>
          {script.stages.map((stage) => (
            <div key={stage.id} className="p-3 rounded-lg bg-muted/30" data-testid={`discovery-stage-${stage.id}`}>
              <p className="text-sm font-semibold">{stage.title}</p>
              <p className="text-xs text-muted-foreground italic mb-1.5">{stage.tip}</p>
              <ul className="space-y-1">
                {stage.lines.map((line, i) => (
                  <li key={i} className="text-sm pl-3 border-l-2 border-primary/30">&ldquo;{line}&rdquo;</li>
                ))}
              </ul>
            </div>
          ))}
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
            <p className="text-xs font-semibold text-amber-800 uppercase mb-1.5">If they object...</p>
            {(script.objection_reframes || []).map((o, i) => (
              <div key={i} className="text-sm mb-1.5">
                <span className="text-muted-foreground">&ldquo;{o.objection}&rdquo;</span> → <span className="italic">{o.reframe}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CallQueueView({ listId, prospect, aiBrief, briefLoading, headers, onBack, onNext, onConvert, onCoachMe }) {
  const [disposition, setDisposition] = useState('');
  const [notes, setNotes] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactRole, setContactRole] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [showSendAnalysis, setShowSendAnalysis] = useState(false);
  const [playbook, setPlaybook] = useState('payment_savings');

  const FOLLOW_UP_REQUIRED = ['callback', 'schedule_meeting', 'schedule_demo', 'visit_store'];

  if (!prospect) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  if (prospect.complete) {
    return (
      <div className="p-4 text-center py-16" data-testid="queue-complete">
        <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-4" />
        <h2 className="text-xl font-bold mb-2">List Complete!</h2>
        <p className="text-muted-foreground text-sm mb-4">All prospects have been worked.</p>
        <div className="flex flex-wrap justify-center gap-2">
          {Object.entries(prospect.stats || {}).map(([k, v]) => (
            <Badge key={k} variant="outline">{k.replace(/_/g, ' ')}: {v}</Badge>
          ))}
        </div>
        <Button className="mt-6" onClick={onBack}>Back to Lists</Button>
      </div>
    );
  }

  const biz = prospect.business;
  const phoneClean = biz.phone?.replace(/[^\d+]/g, '') || '';
  const telLink = phoneClean ? `tel:${phoneClean.startsWith('+') ? phoneClean : '+1' + phoneClean.replace(/^1/, '')}` : null;

  const handleSaveAndNext = async () => {
    if (!disposition) { toast.error('Select a disposition'); return; }
    if (FOLLOW_UP_REQUIRED.includes(disposition) && !followUpDate) {
      toast.error('Follow-up date is required for this disposition');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/prospecting/lists/${listId}/disposition?place_id=${biz.place_id}`, {
        method: 'POST', headers,
        body: JSON.stringify({
          disposition, notes,
          contact_person: contactPerson, contact_role: contactRole,
          follow_up_date: followUpDate || null, follow_up_time: followUpTime || null,
          objective: aiBrief?.objective || null,
        }),
      });
      const data = await r.json();
      if (data.calendar_link) {
        toast.success(
          <span>Saved! <a href={data.calendar_link} target="_blank" rel="noopener noreferrer" className="underline font-medium">Open in Calendar</a></span>,
          { duration: 5000 }
        );
      } else {
        toast.success('Saved!');
      }
      // Reset form
      setDisposition(''); setNotes(''); setContactPerson(''); setContactRole('');
      setFollowUpDate(''); setFollowUpTime('');
      onNext();
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const handleConvert = async () => {
    try {
      const r = await fetch(`${API}/api/prospecting/lists/${listId}/convert/${biz.place_id}`, {
        method: 'POST', headers,
        body: JSON.stringify({ playbook, notes }),
      });
      const data = await r.json();
      toast.success(data.is_existing ? 'Linked to existing lead' : 'Converted to CRM lead!');
      setShowConvert(false);
      onNext();
    } catch { toast.error('Convert failed'); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="call-queue-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
        <div className="text-center">
          <p className="text-xs text-muted-foreground">{prospect.index + 1} of {prospect.total}</p>
          <p className="text-xs text-muted-foreground">{prospect.remaining} remaining</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onCoachMe} data-testid="coach-me-btn">
          <MessageSquare className="w-4 h-4" />
        </Button>
      </div>

      {/* Business Card */}
      <div className="rounded-2xl border bg-card p-4" data-testid="prospect-card">
        <h2 className="text-lg font-bold mb-1">{biz.name}</h2>
        <p className="text-sm text-muted-foreground mb-2">{biz.address}</p>
        <div className="flex items-center gap-3 mb-3">
          {biz.rating && (
            <span className="flex items-center gap-1 text-sm">
              <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" /> {biz.rating}
            </span>
          )}
          {biz.niche && <Badge variant="outline" className="text-xs">{biz.niche.replace(/_/g, ' ')}</Badge>}
          {biz.contact_count > 0 && <Badge variant="secondary" className="text-xs">{biz.contact_count}x contacted</Badge>}
        </div>

        {/* Phone + CALL Button */}
        <div className="flex gap-2 mb-2">
          {telLink ? (
            <a href={telLink} className="flex-1 flex items-center justify-center gap-2 h-12 rounded-xl bg-green-600 text-white font-semibold text-base hover:bg-green-700 transition-colors"
              data-testid="call-button">
              <Phone className="w-5 h-5" /> CALL {biz.phone}
            </a>
          ) : (
            <div className="flex-1 flex items-center justify-center gap-2 h-12 rounded-xl bg-muted text-muted-foreground text-sm">
              <Phone className="w-4 h-4" /> Phone loading...
            </div>
          )}
          {biz.website && (
            <a href={biz.website.startsWith('http') ? biz.website : `https://${biz.website}`}
              target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center w-12 h-12 rounded-xl border bg-card hover:bg-muted transition-colors">
              <Globe className="w-5 h-5" />
            </a>
          )}
        </div>
      </div>

      {/* Quick Actions: Send Analysis */}
      <div className="flex gap-2">
        <Button variant="secondary" className="flex-1 h-10" onClick={() => setShowSendAnalysis(true)} data-testid="send-analysis-btn">
          <Send className="w-4 h-4 mr-2" /> Send Analysis Link
        </Button>
      </div>

      {/* AI Brief */}
      <div className="rounded-2xl border bg-card p-4" data-testid="ai-brief">
        <h3 className="text-xs font-semibold uppercase text-muted-foreground mb-2 flex items-center gap-1.5">
          <Zap className="w-3.5 h-3.5" /> AI Prospect Brief
        </h3>
        {briefLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
            <Loader2 className="w-4 h-4 animate-spin" /> Generating brief...
          </div>
        ) : aiBrief ? (
          <div className="space-y-2 text-sm">
            <div><span className="font-semibold text-xs uppercase text-muted-foreground">Who to ask for:</span> <span className="font-medium">{aiBrief.who_to_ask_for}</span></div>
            <div><span className="font-semibold text-xs uppercase text-muted-foreground">What we know:</span> <span className="whitespace-pre-line">{aiBrief.what_we_know}</span></div>
            <div><span className="font-semibold text-xs uppercase text-muted-foreground">Sales angle:</span> {aiBrief.sales_angle}</div>
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
              <span className="font-semibold text-xs uppercase text-muted-foreground">Suggested opener:</span>
              <p className="mt-1 italic">&ldquo;{aiBrief.suggested_opener}&rdquo;</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
                <span className="text-xs font-semibold text-blue-700 uppercase">Objective</span>
                <p className="font-medium text-blue-900 mt-0.5">{aiBrief.objective?.replace(/_/g, ' ').toUpperCase()}</p>
              </div>
              <div className="p-2 rounded-lg bg-green-50 border border-green-100">
                <span className="text-xs font-semibold text-green-700 uppercase">Next Action</span>
                <p className="font-medium text-green-900 mt-0.5 text-xs">{aiBrief.next_best_action}</p>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Guided Discovery Script — MSC Sales Method */}
      <DiscoveryScriptCard businessName={biz.name} niche={biz.niche} headers={headers} />

      {/* Disposition Form */}
      <div className="rounded-2xl border bg-card p-4 space-y-3" data-testid="disposition-form">
        <h3 className="text-xs font-semibold uppercase text-muted-foreground">What happened?</h3>

        <div className="grid grid-cols-2 gap-2">
          {DISPOSITIONS_UI.map(d => (
            <button key={d.id}
              onClick={() => setDisposition(d.id)}
              className={`p-2.5 rounded-lg text-left text-xs font-medium border transition-all ${
                disposition === d.id
                  ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary'
                  : 'border-border bg-card hover:bg-muted/50'
              }`}
              data-testid={`disp-${d.id}`}>
              {d.label}
            </button>
          ))}
        </div>

        {/* Contact info */}
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Contact name" value={contactPerson}
            onChange={e => setContactPerson(e.target.value)} className="text-sm" data-testid="contact-person" />
          <Select value={contactRole} onValueChange={setContactRole}>
            <SelectTrigger className="text-sm" data-testid="contact-role"><SelectValue placeholder="Role" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="owner">Owner</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="employee">Employee</SelectItem>
              <SelectItem value="gatekeeper">Gatekeeper</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Follow-up date (required for certain dispositions) */}
        {FOLLOW_UP_REQUIRED.includes(disposition) && (
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 space-y-2">
            <p className="text-xs font-semibold text-amber-800 flex items-center gap-1">
              <CalendarDays className="w-3.5 h-3.5" /> Follow-up Required
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)}
                className="text-sm" data-testid="follow-up-date" />
              <Input type="time" value={followUpTime} onChange={e => setFollowUpTime(e.target.value)}
                className="text-sm" data-testid="follow-up-time" />
            </div>
          </div>
        )}

        <Textarea placeholder="Notes..." value={notes} onChange={e => setNotes(e.target.value)}
          rows={2} className="text-sm" data-testid="disposition-notes" />

        {/* Action buttons */}
        <div className="flex gap-2">
          <Button className="flex-1 h-12 text-base font-semibold" onClick={handleSaveAndNext}
            disabled={saving || !disposition} data-testid="save-next-btn">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <>SAVE & NEXT <ArrowRight className="w-5 h-5 ml-2" /></>}
          </Button>
          {['interested', 'schedule_meeting', 'schedule_demo', 'send_analysis'].includes(disposition) && (
            <Button variant="secondary" className="h-12" onClick={() => setShowConvert(true)} data-testid="convert-btn">
              <Briefcase className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Convert Dialog */}
      <Dialog open={showConvert} onOpenChange={setShowConvert}>
        <DialogContent data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle>Convert to Lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">Convert <strong>{biz.name}</strong> to a CRM lead and assign a sales playbook.</p>
            <Select value={playbook} onValueChange={setPlaybook}>
              <SelectTrigger data-testid="playbook-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="payment_savings">Payment Savings</SelectItem>
                <SelectItem value="pos_demo">POS / Software Demo</SelectItem>
                <SelectItem value="nurture">Educate / Nurture</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvert(false)}>Cancel</Button>
            <Button onClick={handleConvert} data-testid="confirm-convert">Convert to Lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Analysis Link Dialog */}
      <SendAnalysisDialog
        open={showSendAnalysis}
        onClose={() => setShowSendAnalysis(false)}
        business={biz}
        listId={listId}
        headers={headers}
        contactPerson={contactPerson}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SEND ANALYSIS LINK DIALOG
// ═══════════════════════════════════════════════════════════

function SendAnalysisDialog({ open, onClose, business, listId, headers, contactPerson }) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [contactName, setContactName] = useState(contactPerson || '');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setContactName(contactPerson || '');
  }, [contactPerson]);

  const handleSend = async () => {
    if (!email && !phone) {
      toast.error('Enter an email or phone number');
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${API}/api/statements/quick-send`, {
        method: 'POST', headers,
        body: JSON.stringify({
          business_name: business?.name || '',
          contact_name: contactName,
          contact_email: email || null,
          contact_phone: phone || null,
          prospect_list_id: listId,
          prospect_place_id: business?.place_id,
          send_email: !!email,
        }),
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.detail || 'Failed to create link');
      }
      const data = await r.json();
      setResult(data);
      if (data.email_sent) {
        toast.success(`Analysis link sent to ${email}`);
      } else {
        toast.success('Analysis link created — copy and send manually');
      }
    } catch (err) { toast.error(err.message); }
    setSending(false);
  };

  const copyLink = () => {
    if (result?.public_url) {
      navigator.clipboard.writeText(result.public_url);
      toast.success('Link copied to clipboard');
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { onClose(); setResult(null); } }}>
      <DialogContent className="max-w-md" data-testid="send-analysis-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            Send Analysis Link
          </DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-green-50 border border-green-200 text-center">
              <CheckCircle2 className="w-8 h-8 mx-auto text-green-600 mb-2" />
              <p className="text-sm font-medium text-green-800">
                {result.email_sent ? 'Link sent via email!' : 'Link created!'}
              </p>
              <p className="text-xs text-green-700 mt-1">Expires in {result.expires_in_days} days</p>
            </div>
            <div className="flex gap-2">
              <Input value={result.public_url} readOnly className="text-xs flex-1" data-testid="analysis-link-url" />
              <Button variant="outline" size="sm" onClick={copyLink} data-testid="copy-link-btn">Copy</Button>
            </div>
            {phone && !result.email_sent && (
              <a href={`sms:${phone}?body=Here's your free processing cost analysis link: ${encodeURIComponent(result.public_url)}`}
                className="flex items-center justify-center gap-2 w-full h-10 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
                data-testid="sms-link-btn">
                <Phone className="w-4 h-4" /> Send via SMS
              </a>
            )}
            <DialogFooter>
              <Button onClick={() => { onClose(); setResult(null); }}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Send <strong>{business?.name}</strong> a link to upload their processing statement for a free cost analysis.
            </p>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Contact Name</label>
              <Input value={contactName} onChange={e => setContactName(e.target.value)}
                placeholder="Owner name" data-testid="analysis-contact-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Email (sends link automatically)</label>
              <Input value={email} onChange={e => setEmail(e.target.value)}
                placeholder="owner@restaurant.com" type="email" data-testid="analysis-email" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Phone (for SMS — wired later)</label>
              <Input value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="(305) 555-0123" data-testid="analysis-phone" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleSend} disabled={sending || (!email && !phone)} data-testid="send-analysis-submit">
                {sending ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Generate & Send</>}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const DISPOSITIONS_UI = [
  { id: 'no_answer', label: 'No Answer' },
  { id: 'gatekeeper', label: 'Gatekeeper' },
  { id: 'spoke_with_employee', label: 'Spoke w/ Employee' },
  { id: 'spoke_with_manager', label: 'Spoke w/ Manager' },
  { id: 'spoke_with_owner', label: 'Spoke w/ Owner' },
  { id: 'send_information', label: 'Send Information' },
  { id: 'send_analysis', label: 'Send Analysis' },
  { id: 'schedule_meeting', label: 'Schedule Meeting' },
  { id: 'schedule_demo', label: 'Schedule Demo' },
  { id: 'visit_store', label: 'Visit Store' },
  { id: 'callback', label: 'Callback' },
  { id: 'interested', label: 'Interested' },
  { id: 'not_interested', label: 'Not Interested' },
];


// ═══════════════════════════════════════════════════════════
// CANVASS D2D VIEW
// ═══════════════════════════════════════════════════════════

function CanvassView({ route, list, headers, onBack, setActiveRoute, loadAiBrief, aiBrief, briefLoading }) {
  const [activeStop, setActiveStop] = useState(null);
  const [disposition, setDisposition] = useState('');
  const [notes, setNotes] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [contactRole, setContactRole] = useState('');
  const [saving, setSaving] = useState(false);

  const toggleTravelMode = async () => {
    const newMode = route.travel_mode === 'driving' ? 'walking' : 'driving';
    try {
      await fetch(`${API}/api/prospecting/routes/${route.route_id}/travel-mode`, {
        method: 'PUT', headers,
        body: JSON.stringify({ travel_mode: newMode }),
      });
      setActiveRoute({ ...route, travel_mode: newMode });
    } catch { toast.error('Failed to update'); }
  };

  const openInMaps = () => {
    const stops = route.stops || [];
    if (stops.length === 0) return;
    const waypoints = stops.map(s => `${s.lat},${s.lng}`).join('/');
    const mode = route.travel_mode === 'walking' ? 'walking' : 'driving';
    window.open(`https://www.google.com/maps/dir/${waypoints}/@${stops[0].lat},${stops[0].lng},14z/data=!4m2!4m1!3e${mode === 'walking' ? '2' : '0'}`, '_blank');
  };

  const handleVisitDisposition = async () => {
    if (!activeStop || !disposition) { toast.error('Select a disposition'); return; }
    const FOLLOW_UP_REQUIRED = ['callback', 'schedule_meeting', 'schedule_demo', 'visit_store'];
    if (FOLLOW_UP_REQUIRED.includes(disposition) && !followUpDate) {
      toast.error('Follow-up date required'); return;
    }
    setSaving(true);
    try {
      await fetch(`${API}/api/prospecting/lists/${list.list_id}/disposition?place_id=${activeStop.place_id}`, {
        method: 'POST', headers,
        body: JSON.stringify({
          disposition, notes,
          contact_person: contactPerson, contact_role: contactRole,
          follow_up_date: followUpDate || null, follow_up_time: followUpTime || null,
        }),
      });
      toast.success('Visit recorded');
      setActiveStop(null); setDisposition(''); setNotes('');
      setContactPerson(''); setContactRole('');
      setFollowUpDate(''); setFollowUpTime('');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  return (
    <div className="p-4 space-y-4" data-testid="canvass-view">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h2 className="text-lg font-bold">Today&apos;s Route</h2>
            <p className="text-xs text-muted-foreground">{route.total_stops} stops · {route.travel_mode}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={toggleTravelMode} data-testid="travel-mode-toggle">
            {route.travel_mode === 'driving' ? <Car className="w-4 h-4 mr-1" /> : <Footprints className="w-4 h-4 mr-1" />}
            {route.travel_mode === 'driving' ? 'Drive' : 'Walk'}
          </Button>
          <Button variant="outline" size="sm" onClick={openInMaps} data-testid="open-maps-btn">
            <Navigation className="w-4 h-4 mr-1" /> Maps
          </Button>
        </div>
      </div>

      {/* Route stops */}
      <div className="space-y-2 max-h-[70vh] overflow-y-auto">
        {(route.stops || []).map((stop, i) => (
          <div key={stop.place_id || i}
            className={`p-3 rounded-xl border transition-colors cursor-pointer ${
              activeStop?.place_id === stop.place_id ? 'border-primary bg-primary/5' : 'bg-card hover:bg-muted/50'
            }`}
            onClick={() => {
              setActiveStop(stop);
              loadAiBrief(stop, [], 'canvass');
            }}
            data-testid={`stop-${i}`}>
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold flex-shrink-0">
                {stop.stop_number || i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{stop.name}</p>
                <p className="text-xs text-muted-foreground truncate">{stop.address}</p>
                {stop.distance_from_prev > 0 && (
                  <p className="text-xs text-muted-foreground mt-0.5">{stop.distance_from_prev} mi from previous</p>
                )}
              </div>
              {stop.phone && (
                <a href={`tel:${stop.phone}`} onClick={e => e.stopPropagation()}
                  className="p-2 rounded-full bg-green-100 text-green-700 flex-shrink-0">
                  <Phone className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Active stop detail + disposition */}
      {activeStop && (
        <Dialog open={!!activeStop} onOpenChange={() => setActiveStop(null)}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="visit-dialog">
            <DialogHeader>
              <DialogTitle className="text-base">{activeStop.name}</DialogTitle>
            </DialogHeader>

            {/* AI Brief */}
            {briefLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading brief...
              </div>
            ) : aiBrief ? (
              <div className="space-y-2 text-sm p-3 rounded-lg bg-muted/50">
                <p><strong>Ask for:</strong> {aiBrief.who_to_ask_for}</p>
                <p><strong>Opener:</strong> <em>&ldquo;{aiBrief.suggested_opener}&rdquo;</em></p>
                <p><strong>Objective:</strong> {aiBrief.objective?.replace(/_/g, ' ').toUpperCase()}</p>
              </div>
            ) : null}

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase text-muted-foreground">Visit Outcome</p>
              <div className="grid grid-cols-2 gap-2">
                {DISPOSITIONS_UI.map(d => (
                  <button key={d.id} onClick={() => setDisposition(d.id)}
                    className={`p-2 rounded-lg text-xs font-medium border transition-all ${
                      disposition === d.id ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted/50'
                    }`}>{d.label}</button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Contact name" value={contactPerson} onChange={e => setContactPerson(e.target.value)} className="text-sm" />
                <Select value={contactRole} onValueChange={setContactRole}>
                  <SelectTrigger className="text-sm"><SelectValue placeholder="Role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {['callback','schedule_meeting','schedule_demo','visit_store'].includes(disposition) && (
                <div className="grid grid-cols-2 gap-2">
                  <Input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} className="text-sm" />
                  <Input type="time" value={followUpTime} onChange={e => setFollowUpTime(e.target.value)} className="text-sm" />
                </div>
              )}

              <Textarea placeholder="Notes..." value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="text-sm" />
            </div>

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setActiveStop(null)}>Cancel</Button>
              <Button onClick={handleVisitDisposition} disabled={saving || !disposition} data-testid="save-visit-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save & Next Stop'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// COACH ME — Real-time objection handling
// ═══════════════════════════════════════════════════════════

function CoachMeView({ headers, business, onBack }) {
  const [situation, setSituation] = useState('');
  const [coaching, setCoaching] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCoach = async () => {
    if (!situation.trim()) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/prospecting/coach-me`, {
        method: 'POST', headers,
        body: JSON.stringify({
          situation,
          business_name: business?.name,
          niche: business?.niche,
        }),
      });
      const data = await r.json();
      setCoaching(data.coaching);
    } catch { toast.error('Coach failed'); }
    setLoading(false);
  };

  return (
    <div className="p-4 space-y-4" data-testid="coach-me-view">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="w-5 h-5" /></Button>
        <div>
          <h2 className="text-lg font-bold">Coach Me</h2>
          <p className="text-xs text-muted-foreground">Tell me what the merchant said</p>
        </div>
      </div>

      <Textarea
        placeholder=        'e.g. "Merchant says he loves Toast but processing is expensive"'
        value={situation} onChange={e => setSituation(e.target.value)}
        rows={3} className="text-base" data-testid="coach-input"
      />

      <Button className="w-full h-12" onClick={handleCoach} disabled={loading || !situation.trim()} data-testid="coach-submit">
        {loading ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Thinking...</> : <><Zap className="w-5 h-5 mr-2" /> COACH ME</>}
      </Button>

      {coaching && (
        <div className="rounded-2xl border bg-card p-4 whitespace-pre-wrap text-sm leading-relaxed" data-testid="coach-response">
          {coaching}
        </div>
      )}

      {/* Quick objections */}
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase text-muted-foreground">Common Objections</p>
        {[
          "We're happy with our processor",
          "Just send me information",
          "I need to think about it",
          "We already use Square/Clover/Toast",
          "It is too expensive",
          "I need to talk to my partner",
          "We're under contract",
        ].map(obj => (
          <button key={obj} onClick={() => { setSituation(`Merchant says: "${obj}"`); }}
            className="w-full text-left p-2.5 rounded-lg border bg-card hover:bg-muted/50 text-sm transition-colors">
            &ldquo;{obj}&rdquo;
          </button>
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════

function StatusDot({ status }) {
  const colors = {
    not_contacted: 'bg-gray-300',
    attempted: 'bg-yellow-400',
    connected: 'bg-blue-400',
    follow_up: 'bg-amber-400',
    interested: 'bg-green-400',
    appointment: 'bg-purple-400',
    converted: 'bg-emerald-500',
    not_interested: 'bg-red-400',
  };
  return <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${colors[status] || 'bg-gray-300'}`} />;
}

const DISPOSITIONS = [
  'no_answer', 'gatekeeper', 'spoke_with_employee', 'spoke_with_manager',
  'spoke_with_owner', 'send_information', 'send_analysis', 'schedule_meeting',
  'schedule_demo', 'visit_store', 'callback', 'interested', 'not_interested',
];


// ═══════════════════════════════════════════════════════════
// PERFORMANCE DASHBOARD
// ═══════════════════════════════════════════════════════════

function PerformanceView({ headers, onBack }) {
  const { user } = useAuthStore();
  const isManager = ['super_admin', 'iso'].includes(user?.role);
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/prospecting/performance?period=${period}`, { headers }).then(r => r.json()),
      fetch(`${API}/api/prospecting/usage`, { headers }).then(r => r.json()),
    ]).then(([perf, usg]) => {
      setData(perf);
      setUsage(usg);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, [period]);

  const m = data?.metrics || {};

  return (
    <div className="p-4 space-y-5" data-testid="performance-view">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={onBack} data-testid="perf-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1">
          <h2 className="text-lg font-bold flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Performance</h2>
          <p className="text-xs text-muted-foreground">Prospecting activity metrics</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-24" data-testid="period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">7 days</SelectItem>
            <SelectItem value="30d">30 days</SelectItem>
            <SelectItem value="90d">90 days</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Funnel Metrics */}
          <div className="grid grid-cols-3 gap-2" data-testid="perf-metrics">
            <MetricCard label="Prospects" value={m.prospects_added} color="blue" />
            <MetricCard label="Activities" value={m.total_activities} color="indigo" />
            <MetricCard label="Calls" value={m.calls} color="purple" />
            <MetricCard label="DMs Reached" value={m.dm_reached} color="amber" />
            <MetricCard label="Links Sent" value={m.links_sent} color="cyan" />
            <MetricCard label="Meetings" value={m.meetings_booked} color="green" />
            <MetricCard label="Demos" value={m.demos_booked} color="teal" />
            <MetricCard label="Leads" value={m.leads_converted} color="emerald" />
            <MetricCard label="Boarded" value={m.boarded} color="green" />
          </div>

          {/* Google Places Usage */}
          {usage && (
            <div className="rounded-2xl border bg-card p-4" data-testid="places-usage">
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Globe className="w-4 h-4" /> Google Places Usage
              </h3>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, (usage.searches_used / usage.monthly_limit) * 100)}%`,
                        background: usage.remaining < 10 ? '#ef4444' : usage.remaining < 30 ? '#f59e0b' : '#22c55e',
                      }}
                    />
                  </div>
                </div>
                <span className="text-xs font-mono whitespace-nowrap">
                  {usage.searches_used} / {usage.monthly_limit}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {usage.remaining} searches remaining this month · {usage.cached_places} places cached
              </p>
            </div>
          )}

          {/* Disposition Breakdown */}
          {data?.disposition_breakdown && Object.keys(data.disposition_breakdown).length > 0 && (
            <div className="rounded-2xl border bg-card p-4" data-testid="disposition-breakdown">
              <h3 className="text-sm font-semibold mb-3">Disposition Breakdown</h3>
              <div className="space-y-1.5">
                {Object.entries(data.disposition_breakdown)
                  .sort(([, a], [, b]) => b - a)
                  .map(([disp, count]) => (
                    <div key={disp} className="flex items-center gap-2 text-xs">
                      <span className="flex-1 truncate capitalize">{disp.replace(/_/g, ' ')}</span>
                      <div className="w-24 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-primary/60" style={{ width: `${(count / m.total_activities) * 100}%` }} />
                      </div>
                      <span className="font-mono w-8 text-right">{count}</span>
                    </div>
                  ))
                }
              </div>
            </div>
          )}

          {/* Agent Breakdown (ISO/Admin only) */}
          {isManager && data?.agent_breakdown?.length > 0 && (
            <div className="rounded-2xl border bg-card p-4" data-testid="agent-breakdown">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" /> Agent Leaderboard
              </h3>
              <div className="space-y-2">
                {data.agent_breakdown.map((agent, i) => (
                  <div key={agent.user_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/40 text-xs">
                    <span className="font-bold text-sm w-5 text-center">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{agent.name}</p>
                      <p className="text-muted-foreground">
                        {agent.total_activities} activities · {agent.dm_reached} DMs · {agent.meetings + agent.demos} booked
                      </p>
                    </div>
                    <span className="font-mono">{agent.links_sent} sent</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MetricCard({ label, value, color }) {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    purple: 'bg-purple-50 text-purple-700 border-purple-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    cyan: 'bg-cyan-50 text-cyan-700 border-cyan-100',
    green: 'bg-green-50 text-green-700 border-green-100',
    teal: 'bg-teal-50 text-teal-700 border-teal-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  };
  return (
    <div className={`rounded-xl border p-3 text-center ${colorMap[color] || colorMap.blue}`} data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <p className="text-2xl font-bold">{value || 0}</p>
      <p className="text-[10px] font-medium uppercase tracking-wide opacity-70">{label}</p>
    </div>
  );
}
